import { api, ApiError, type Article, type ArticleInput, type Section } from '../api.ts';
import { Preview } from '../preview.ts';
import { RichText } from '../richtext.ts';
import { confirmDialog, debounce, el, field, select, toast } from '../ui.ts';

export async function articlesList(): Promise<HTMLElement> {
  const articles = await api.articles();
  const rows = articles.map((a) => el('tr', {},
    el('td', {}, el('a', { href: `#/articles/${a.id}` }, a.headline || '(untitled)')),
    el('td', {}, el('span', { class: `status ${a.status}` }, a.status)),
    el('td', {}, a.category),
    el('td', {}, String(a.priority)),
    el('td', {}, a.author ?? ''),
    el('td', {}, new Date(a.updatedAt).toLocaleString()),
  ));
  return el('section', {},
    el('div', { class: 'head' }, el('h1', {}, 'Articles'), el('a', { class: 'button primary', href: '#/articles/new' }, 'New article')),
    articles.length
      ? el('table', {}, el('thead', {}, el('tr', {}, ['Headline', 'Status', 'Category', 'Priority', 'Author', 'Updated'].map((h) => el('th', {}, h)))), el('tbody', {}, rows))
      : el('p', { class: 'muted' }, 'No articles yet. Write one and watch the preview — the compiler shows how it will look on air.'),
  );
}

const toLocalInput = (iso: string | null) => (iso ? new Date(new Date(iso).getTime() - new Date().getTimezoneOffset() * 60_000).toISOString().slice(0, 16) : '');
const fromLocalInput = (v: string) => (v ? new Date(v).toISOString() : null);

export async function articleEditor(id: string | null): Promise<HTMLElement> {
  const [sections, existing] = await Promise.all([api.sections(), id ? api.article(id) : Promise.resolve(null)]);
  let article: Article | null = existing;
  const categories = [...new Set(sections.flatMap((s) => s.categories))];
  const categoryFor = (s: Section) => s.categories[0] ?? 'news';

  const headline = el('input', { type: 'text', value: article?.headline ?? '', placeholder: 'Headline', maxlength: '200' });
  const standfirst = el('textarea', { rows: '2', placeholder: 'Standfirst (one or two sentences)' }, article?.standfirst ?? '');
  const category = select(
    [...categories.map((c) => ({ value: c, label: c })), ...(article && !categories.includes(article.category) ? [{ value: article.category, label: article.category }] : [])],
    article?.category ?? (sections[0] ? categoryFor(sections[0]) : 'news'),
  );
  const region = el('input', { type: 'text', value: article?.region ?? '', placeholder: 'optional, e.g. uk' });
  const priority = select([5, 4, 3, 2, 1].map((n) => ({ value: String(n), label: `${n}${n === 5 ? ' (top)' : n === 1 ? ' (low)' : ''}` })), String(article?.priority ?? 3));
  const publishAt = el('input', { type: 'datetime-local', value: toLocalInput(article?.publishAt ?? null) });
  const expireAt = el('input', { type: 'datetime-local', value: toLocalInput(article?.expireAt ?? null) });
  const preview = new Preview(9);
  const status = el('span', { class: `status ${article?.status ?? 'draft'}` }, article?.status ?? 'new');

  const input = (): ArticleInput => ({
    headline: headline.value.trim(),
    standfirst: standfirst.value.trim(),
    body: body.blocks(),
    category: category.value,
    region: region.value.trim() || null,
    priority: Number(priority.value),
    publishAt: fromLocalInput(publishAt.value),
    expireAt: fromLocalInput(expireAt.value),
    relatedPages: article?.relatedPages ?? [],
  });

  const refresh = debounce(async () => {
    try {
      const a = input();
      if (!a.headline) { preview.blank(); return; }
      const sectionKey = sections.find((s) => s.categories.includes(a.category))?.key;
      preview.show(await api.preview({ kind: 'article', article: a, sectionKey }));
    } catch (e) {
      toast(e instanceof ApiError ? e.message : 'preview failed', 'error');
    }
  }, 350);
  const body = new RichText(article?.body ?? [], refresh);
  for (const i of [headline, standfirst, category, region, priority]) i.addEventListener('input', refresh);

  const save = async (): Promise<Article | null> => {
    const a = input();
    if (!a.headline) { toast('A headline is required', 'error'); return null; }
    try {
      article = article ? await api.updateArticle(article.id, a) : await api.createArticle(a);
      status.textContent = article.status;
      status.className = `status ${article.status}`;
      if (location.hash !== `#/articles/${article.id}`) history.replaceState(null, '', `#/articles/${article.id}`);
      toast('Saved');
      return article;
    } catch (e) {
      toast(e instanceof ApiError ? e.message : 'save failed', 'error');
      return null;
    }
  };

  const actions = el('div', { class: 'actions' },
    el('button', { type: 'button', class: 'primary', onClick: () => void save() }, 'Save'),
    el('button', { type: 'button', onClick: async () => {
      const a = await save();
      if (!a) return;
      try { article = await api.publish(a.id); status.textContent = article.status; status.className = `status ${article.status}`; toast('Published — the section re-allocates within a few seconds'); }
      catch (e) { toast(e instanceof ApiError ? e.message : 'publish failed', 'error'); }
    } }, 'Publish'),
    el('button', { type: 'button', onClick: async () => {
      if (!article) return;
      try { article = await api.unpublish(article.id); status.textContent = article.status; status.className = `status ${article.status}`; toast('Unpublished'); }
      catch (e) { toast(e instanceof ApiError ? e.message : 'failed', 'error'); }
    } }, 'Unpublish'),
    el('button', { type: 'button', class: 'danger', onClick: async () => {
      if (!article || !confirmDialog('Delete this article?')) return;
      await api.deleteArticle(article.id);
      location.hash = '#/articles';
    } }, 'Delete'),
  );

  void refresh();
  return el('section', { class: 'editor' },
    el('div', { class: 'head' }, el('h1', {}, id ? 'Edit article' : 'New article'), status),
    el('div', { class: 'cols' },
      el('div', { class: 'form' },
        field('Headline', headline, 'Double height on the band; wraps to two rows, longer falls back to normal height.'),
        field('Standfirst', standfirst),
        el('div', { class: 'frow' }, field('Category', category, 'Decides which section lays it out.'), field('Region', region), field('Priority', priority)),
        el('div', { class: 'frow' }, field('Publish at', publishAt, 'Leave empty to publish immediately.'), field('Expire at', expireAt, 'Default 30 days after publishing.')),
        field('Body', body.root, 'Bold becomes a colour change, links become click targets, lists indent. Text is never cut.'),
        actions,
      ),
      preview.root,
    ),
  );
}
